import {RefObject, useCallback, useEffect, useRef, useState} from 'react';
import {WebcamRefTypes} from '@chekinapp/ui';
import {Analytics, ANALYTIC_EVENTS} from '../../analytics';
import {useAdaptiveFaceDetection} from '../faceDetection';

const CENTER_HOLD_MS = 1200; // face must stay centered this long before capture
const AUTO_GIVEUP_MS = 10000; // reveal manual button if auto hasn't captured by now

type UseAutoFaceCaptureProps = {
  videoRef: RefObject<WebcamRefTypes>;
  enabled: boolean;
  onCapture: () => void;
};

export function useAutoFaceCapture({
  videoRef,
  enabled,
  onCapture,
}: UseAutoFaceCaptureProps) {
  const {isModelLoading, facePosition, startDetection, isUsingClientDetection} =
    useAdaptiveFaceDetection({
      videoRef,
      enabled,
      forceMechanism: 'AUTO',
    });

  const holdTimer = useRef<NodeJS.Timeout | null>(null);
  const detectionStartedAt = useRef<number | null>(null);
  const hasCaptured = useRef(false);
  const mechanismTracked = useRef(false);
  const [manualFallback, setManualFallback] = useState(false);

  const mechanism = isUsingClientDetection ? 'CLIENT' : 'SERVER';

  useEffect(() => {
    if (!enabled || isUsingClientDetection === undefined || mechanismTracked.current) {
      return;
    }
    mechanismTracked.current = true;
    Analytics.track(ANALYTIC_EVENTS.ivFaceDetectionMechanism, {
      flow: 'selfie',
      mechanism,
    });
  }, [enabled, isUsingClientDetection, mechanism]);

  const start = useCallback(() => {
    if (!enabled) return;
    hasCaptured.current = false;
    if (!detectionStartedAt.current) detectionStartedAt.current = Date.now();
    startDetection();
  }, [enabled, startDetection]);

  useEffect(() => {
    if (!enabled || isModelLoading) return;
    startDetection();
  }, [enabled, isModelLoading, startDetection]);

  useEffect(() => {
    if (!enabled || isModelLoading || hasCaptured.current || manualFallback) {
      if (holdTimer.current) {
        clearTimeout(holdTimer.current);
        holdTimer.current = null;
      }
      return;
    }

    if (facePosition === 'center') {
      if (!holdTimer.current) {
        holdTimer.current = setTimeout(() => {
          hasCaptured.current = true;
          holdTimer.current = null;
          Analytics.track(ANALYTIC_EVENTS.ivSelfieAutoCaptured, {
            mechanism,
            ms_to_capture: detectionStartedAt.current
              ? Date.now() - detectionStartedAt.current
              : null,
          });
          onCapture();
        }, CENTER_HOLD_MS);
      }
    } else if (holdTimer.current) {
      clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
  }, [enabled, isModelLoading, facePosition, manualFallback, onCapture, mechanism]);

  useEffect(() => {
    if (!enabled) return;

    const timeout = setTimeout(() => {
      if (!hasCaptured.current) setManualFallback(true);
    }, AUTO_GIVEUP_MS);
    return () => clearTimeout(timeout);
  }, [enabled]);

  useEffect(
    () => () => {
      if (holdTimer.current) clearTimeout(holdTimer.current);
    },
    [],
  );

  return {isModelLoading, facePosition, manualFallback, isUsingClientDetection, start};
}
